import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col } from "react-bootstrap";
import PageTitle from "../../components/PageTitle";
import { ActionButton, Buttons } from "../../components/Buttons";
import { TextInputForm } from "../../components/Forms";
import { FaMagnifyingGlass } from "react-icons/fa6";
import TableUI from "../../components/TableUI";
import CustomModal from "../../components/Modal";
import { MdOutlineDelete } from "react-icons/md";
import { TbCircleLetterI } from "react-icons/tb";
import { HiOutlineDotsVertical } from "react-icons/hi";
import UnitCreations from "./UnitCreation";
import {
  fetchUnit,
  createUnit,
  updateUnit,
  deleteUnit,
} from "../../slices/unitSlice";
import NotifyData from "../../components/NotifyData";
import Pagnation from "../../components/Pagnation";

const Unit = () => {
  const dispatch = useDispatch();
  const { unit } = useSelector((state) => state.unit);

  const [formData, setFormData] = useState({});
  const [show, setShow] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // 🔹 Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    dispatch(fetchUnit());
  }, [dispatch]);

  // 🔹 Open / Close Modal
  const handleOpen = () => setShow(true);
  const handleClose = () => setShow(false);

  // 🔹 Edit / Create
  const handleEdit = (unit) => {
    setEditMode(true);
    setSelectedUnit(unit);
    setFormData({ unit_type: unit.unit_type });
    handleOpen();
  };

  const handleCreate = () => {
    setEditMode(false);
    setFormData({});
    handleOpen();
  };

  // 🔹 Submit Create / Update
  const handleSubmit = async () => {
    try {
      if (editMode) {
        await dispatch(
          updateUnit({ id: selectedUnit.id, unit_type: formData.unit_type })
        ).unwrap();
        NotifyData("Unit Updated Successfully", "success");
      } else {
        await dispatch(createUnit(formData)).unwrap();
        NotifyData("Unit Created Successfully", "success");
      }
    } catch (error) {
      NotifyData(
        editMode ? "Unit Update Failed" : "Unit Creation Failed",
        "error"
      );
    }

    handleClose();
    setFormData({});
    setEditMode(false);
  };

  // 🔹 Delete
  const handleDelete = async (id) => {
    try {
      await dispatch(deleteUnit(id)).unwrap();
      NotifyData("Unit Deleted Successfully", "success");
    } catch {
      NotifyData("Unit Deletion Failed", "error");
    }
  };

  // 🔹 Search Filter
  const filteredUnits =
    unit?.filter((item) =>
      item?.unit_type?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  // 🔹 Pagination Logic
  const total = filteredUnits.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = filteredUnits.slice(startIndex, endIndex);

  // 🔹 Table Headers & Data
  const RoleHead = ["No", "Unit"];
  const RoleData =
    currentData?.length > 0
      ? currentData.map((item, index) => ({
          values: [
            startIndex + index + 1,
            item.unit_type,
            <ActionButton
              options={[
                {
                  label: "Edit",
                  icon: <TbCircleLetterI />,
                  onClick: () => handleEdit(item),
                },
                {
                  label: "Delete",
                  icon: <MdOutlineDelete />,
                  onClick: () => handleDelete(item.id),
                },
              ]}
              label={<HiOutlineDotsVertical />}
            />,
          ],
        }))
      : [];

  return (
    <div id="main">
      <Container fluid>
        <Row>
          {/* Page Title */}
          <Col lg="6" className="py-3">
            <PageTitle PageTitle="Unit" showButton={false} />
          </Col>

          {/* Create Button */}
          <Col lg="6" className="py-3 text-end">
            <Buttons
              label="Create New"
              classname="crud-btn"
              OnClick={handleCreate}
            />
          </Col>

          {/* Search Input */}
          <Col lg="3" className="align-self-center">
            <TextInputForm
              prefix_icon={<FaMagnifyingGlass />}
              PlaceHolder="Search Unit Name"
              className="form-control-padleft"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // reset page when search changes
              }}
            />
          </Col>

          {/* Table */}
          <Col lg="12" xs="12">
            <TableUI headers={RoleHead} body={RoleData} className="table-end" />
          </Col>

          {/* Pagination */}
          <Col lg="12" xs="12" className="d-flex justify-content-end py-3">
            <Pagnation
              total={total}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={(page) => setCurrentPage(page)}
              onPageSizeChange={(current, size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </Col>
        </Row>
      </Container>

      {/* Modal */}
      <CustomModal
        show={show}
        setShow={setShow}
        pageTitle={editMode ? <>Edit Unit</> : <>Create Unit</>}
        showButton={true}
        submitButton={true}
        label={editMode ? <>Update</> : <>Submit</>}
        CancelLabel={<>Cancel</>}
        BodyComponent={
          <UnitCreations formData={formData} setFormData={setFormData} />
        }
        OnClick={handleSubmit}
        Size="md"
        handleOpen={handleOpen}
        handleClose={handleClose}
      />
    </div>
  );
};

export default Unit;
